// THE HAND-OFF PACKET (Stage 4.5, guide §6.10) — what the person the assistant passed a customer to is shown: why, in the
// assistant's own words (written with the transfer), what it already did, the conversation so far, and — once recorded — what
// came of it. Facts only (the owner's decision 1): nothing here was written for the packet. The same card on a ringing call,
// beside a WhatsApp chat, and on the conversation's record.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Bot, CheckCircle2, ChevronDown, ChevronUp, MessagesSquare } from "lucide-react";

import { relativeTime, dayAndTime } from "../../../utils/times";
import { getHandoff } from "./handoffApi";
import { doneLabel, iconOfDone } from "./handoffMeta";

const ui = { fontFamily: "var(--font-ui)" };
const label = { ...ui, fontSize: "var(--t-caption)", fontWeight: 700, color: "var(--ink-500)", textTransform: "uppercase", letterSpacing: "0.04em" };

// "[Caller]: …" / "[AI Agent]: …" — the record's own line shape — with the speaker in the screen's language.
function Line({ line, t }) {
  const m = /^\[(Caller|AI Agent|Agent|Customer|Staff)\]:\s*([\s\S]*)$/.exec(line);
  const who = m ? { Caller: "caller", Customer: "caller", "AI Agent": "ai", Agent: "staff", Staff: "staff" }[m[1]] : null;
  return (
    <Box sx={{ display: "flex", gap: "var(--sp-2)", py: "2px" }}>
      {who && <Typography component="span" sx={{ ...ui, fontSize: "var(--t-caption)", fontWeight: 700, color: who === "ai" ? "var(--c-violet)" : "var(--ink-600)", flexShrink: 0, minWidth: 64 }}>{t(`speaker.${who}`)}</Typography>}
      <Typography component="span" dir="auto" sx={{ ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-800)", overflowWrap: "anywhere" }}>{m ? m[2] : line}</Typography>
    </Box>
  );
}

export default function HandoffCard({ handoff, compact = false, footer = null }) {
  const { t, i18n } = useTranslation("handoff");
  const [lines, setLines] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!handoff) return null;
  const done = handoff.done ?? [];
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (lines == null) {
      setLoading(true);
      try { setLines((await getHandoff(handoff.id))?.lines ?? []); } catch { setLines([]); }
      finally { setLoading(false); }
    }
  };
  return (
    <Box component="section" aria-label={t("title")}
      sx={{ p: compact ? "var(--sp-3)" : "var(--sp-4)", borderRadius: "var(--r-lg)", border: "1px solid var(--line-100)", borderInlineStart: "3px solid var(--c-violet)",
        bgcolor: "var(--surface-card)", boxShadow: compact ? "none" : "var(--shadow-card)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flexWrap: "wrap", mb: "var(--sp-2)" }}>
        <Box aria-hidden="true" sx={{ width: 28, height: 28, borderRadius: "var(--r-md)", display: "grid", placeItems: "center", bgcolor: "var(--c-violet-bg)", color: "var(--c-violet)", flexShrink: 0 }}>
          <Bot size={15} />
        </Box>
        <Typography component="h3" sx={{ ...ui, fontSize: "var(--t-body-sm)", fontWeight: 700, color: "var(--ink-900)" }}>{t("title")}</Typography>
        {handoff.section && (
          <Box component="span" sx={{ ...ui, px: "8px", py: "2px", borderRadius: "var(--r-pill)", bgcolor: "var(--surface-2)", color: "var(--ink-600)", fontSize: "var(--t-caption)", fontWeight: 600 }}>
            {handoff.section}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {handoff.createdAt && (
          <Typography component="span" title={dayAndTime(handoff.createdAt, i18n.language)} sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-400)" }}>
            {relativeTime(handoff.createdAt, i18n.language)}
          </Typography>
        )}
      </Box>

      <Typography sx={label}>{t("why")}</Typography>
      <Typography dir="auto" sx={{ ...ui, fontSize: "var(--t-body)", color: handoff.reason ? "var(--ink-900)" : "var(--ink-400)", mt: "2px", mb: "var(--sp-3)", overflowWrap: "anywhere" }}>
        {handoff.reason || t("noReason")}
      </Typography>

      <Typography sx={label}>{t("didTitle")}</Typography>
      {done.length === 0 ? (
        <Typography sx={{ ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-400)", mt: "2px" }}>{t("didNothing")}</Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, mt: "var(--sp-1)" }}>
          {done.map((d, i) => {
            const Icon = iconOfDone(d);
            return (
              <Box component="li" key={`${d.action}-${d.ref ?? i}`} sx={{ listStyle: "none", display: "flex", alignItems: "center", gap: "var(--sp-2)", py: "2px" }}>
                <Icon size={14} aria-hidden="true" color="var(--c-violet)" />
                <Typography component="span" sx={{ ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-800)" }}>{doneLabel(d, t)}</Typography>
                {d.ref && <Typography component="span" sx={{ ...ui, fontFamily: "var(--font-mono, monospace)", fontSize: "var(--t-caption)", color: "var(--ink-500)" }}>{d.ref}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}

      {handoff.channel === "voice" && (
        <Box sx={{ mt: "var(--sp-3)" }}>
          <Box component="button" type="button" onClick={toggle} aria-expanded={open}
            sx={{ ...ui, display: "inline-flex", alignItems: "center", gap: "6px", minHeight: 32, px: "10px", border: "1px solid var(--line-200)", borderRadius: "var(--r-pill)",
              bgcolor: "var(--surface-card)", color: "var(--ink-800)", fontSize: "var(--t-body-sm)", cursor: "pointer", "&:focus-visible": { outline: "2px solid var(--brand-blue)", outlineOffset: 2 } }}>
            <MessagesSquare size={14} aria-hidden="true" /> {t(open ? "hideConversation" : "showConversation")}
            {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
          </Box>
          {open && (
            <Box sx={{ mt: "var(--sp-2)", p: "var(--sp-3)", bgcolor: "var(--surface-2)", borderRadius: "var(--r-md)", maxHeight: 260, overflowY: "auto" }}>
              {loading ? <CircularProgress size={18} sx={{ color: "var(--brand-violet)" }} />
                : lines?.length ? lines.map((l, i) => <Line key={i} line={l} t={t} />)
                  : <Typography sx={{ ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-400)" }}>{t("noConversation")}</Typography>}
            </Box>
          )}
        </Box>
      )}

      {handoff.status === "done" && (handoff.outcome || handoff.note) && (
        <Box sx={{ mt: "var(--sp-3)", pt: "var(--sp-2)", borderTop: "1px solid var(--line-100)" }}>
          <Typography sx={{ ...label, display: "flex", alignItems: "center", gap: "4px" }}><CheckCircle2 size={12} aria-hidden="true" color="var(--success)" /> {t("outcomeTitle")}</Typography>
          {handoff.outcome && <Typography sx={{ ...ui, fontSize: "var(--t-body-sm)", fontWeight: 700, color: "var(--ink-900)", mt: "2px" }}>{handoff.outcome}</Typography>}
          {handoff.note && <Typography dir="auto" sx={{ ...ui, fontSize: "var(--t-body-sm)", color: "var(--ink-800)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{handoff.note}</Typography>}
          {handoff.outcomeAt && <Typography sx={{ ...ui, fontSize: "var(--t-caption)", color: "var(--ink-400)" }}>{dayAndTime(handoff.outcomeAt, i18n.language)}</Typography>}
        </Box>
      )}
      {footer}
    </Box>
  );
}
